import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { UserProfile, CreateProfileInput, ScoringSex } from '../domain/users/types';
import { normalizeEmail } from '../domain/users/types';
import { db } from '../infrastructure/firebase/config';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';

const LOCAL_STORAGE_EMAIL_KEY = 'swol_user_email';
const LOCAL_STORAGE_USER_KEY = 'swol_user_profile';

interface UserContextType {
  user: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  emailNotFound: boolean;
  login: (email: string) => Promise<void>;
  createProfile: (data: CreateProfileInput) => Promise<void>;
  updateProfile: (
    updates: Partial<Pick<UserProfile, 'displayName' | 'bodyWeightLb' | 'scoringSex'>>,
  ) => Promise<void>;
  logout: () => void;
}

const UserContext = createContext<UserContextType | null>(null);

/** Read the cached profile from localStorage (synchronous, runs once) */
function getCachedProfile(): UserProfile | null {
  try {
    const cached = localStorage.getItem(LOCAL_STORAGE_USER_KEY);
    if (cached) {
      return JSON.parse(cached) as UserProfile;
    }
  } catch {
    localStorage.removeItem(LOCAL_STORAGE_USER_KEY);
  }
  return null;
}

export function useUser(): UserContextType {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const [user, setUser] = useState<UserProfile | null>(() => getCachedProfile());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailNotFound, setEmailNotFound] = useState(false);

  async function lookupUserByEmail(email: string): Promise<boolean> {
    try {
      const normalized = normalizeEmail(email);
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('normalizedEmail', '==', normalized));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const userDoc = snapshot.docs[0];
        const data = userDoc.data();
        const profile: UserProfile = {
          id: userDoc.id,
          normalizedEmail: data.normalizedEmail,
          displayName: data.displayName,
          bodyWeightLb: data.bodyWeightLb,
          scoringSex: data.scoringSex as ScoringSex,
          createdAt: data.createdAt?.toDate?.() ?? new Date(),
          updatedAt: data.updatedAt?.toDate?.() ?? new Date(),
        };

        setUser(profile);
        localStorage.setItem(LOCAL_STORAGE_EMAIL_KEY, normalized);
        localStorage.setItem(LOCAL_STORAGE_USER_KEY, JSON.stringify(profile));
        setEmailNotFound(false);
        return true;
      }

      setEmailNotFound(true);
      return false;
    } catch (err) {
      console.error('Error looking up user:', err);
      setError('Could not reach the server. Please check your connection and try again.');
      return false;
    }
  }

  const login = useCallback(async (email: string) => {
    setIsLoading(true);
    setError(null);
    setEmailNotFound(false);

    const found = await lookupUserByEmail(email);
    if (!found) {
      localStorage.setItem(LOCAL_STORAGE_EMAIL_KEY, normalizeEmail(email));
    }

    setIsLoading(false);
  }, []);

  const createProfile = useCallback(async (data: CreateProfileInput) => {
    setIsLoading(true);
    setError(null);

    try {
      const normalized = normalizeEmail(data.email);
      const usersRef = collection(db, 'users');

      const docRef = await addDoc(usersRef, {
        normalizedEmail: normalized,
        displayName: data.displayName,
        bodyWeightLb: data.bodyWeightLb,
        scoringSex: data.scoringSex,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const newDoc = await getDoc(doc(db, 'users', docRef.id));
      const newData = newDoc.data();

      const profile: UserProfile = {
        id: docRef.id,
        normalizedEmail: normalized,
        displayName: data.displayName,
        bodyWeightLb: data.bodyWeightLb,
        scoringSex: data.scoringSex,
        createdAt: newData?.createdAt?.toDate?.() ?? new Date(),
        updatedAt: newData?.updatedAt?.toDate?.() ?? new Date(),
      };

      setUser(profile);
      setEmailNotFound(false);
      localStorage.setItem(LOCAL_STORAGE_EMAIL_KEY, normalized);
      localStorage.setItem(LOCAL_STORAGE_USER_KEY, JSON.stringify(profile));
    } catch (err) {
      console.error('Error creating profile:', err);
      setError('Could not create profile. Please check your connection and try again.');
    }

    setIsLoading(false);
  }, []);

  const updateProfile = useCallback(
    async (updates: Partial<Pick<UserProfile, 'displayName' | 'bodyWeightLb' | 'scoringSex'>>) => {
      if (!user) return;
      setIsLoading(true);
      setError(null);

      try {
        const userRef = doc(db, 'users', user.id);
        await updateDoc(userRef, {
          ...updates,
          updatedAt: serverTimestamp(),
        });

        const updatedUser = { ...user, ...updates, updatedAt: new Date() };
        setUser(updatedUser);
        localStorage.setItem(LOCAL_STORAGE_USER_KEY, JSON.stringify(updatedUser));
      } catch (err) {
        console.error('Error updating profile:', err);
        setError('Could not update profile. Please check your connection and try again.');
      }

      setIsLoading(false);
    },
    [user],
  );

  const logout = useCallback(() => {
    setUser(null);
    setEmailNotFound(false);
    setError(null);
    localStorage.removeItem(LOCAL_STORAGE_EMAIL_KEY);
    localStorage.removeItem(LOCAL_STORAGE_USER_KEY);
  }, []);

  return (
    <UserContext
      value={{ user, isLoading, error, emailNotFound, login, createProfile, updateProfile, logout }}
    >
      {children}
    </UserContext>
  );
}
